import { useCallback, useEffect, useRef, useState } from 'react';
import { Image } from 'antd';
import { klona } from 'klona';
import { Icon } from '@fuxi/eevee-icon';
import Guide from '@/assets/icon/Guide.svg';
import DeleteIcon from '@/assets/icon/Delete.svg';
import { useCustomCamera } from '@/hooks';
import { Toast } from '@/utils';
import { storagePrefixFixedV2 } from '@/constant';
import { ImagePreviewCompare } from '../ImagePreviewCompare';
import cx from './index.module.scss';

const CAMERA_MARK_URL: Record<CameraMarkType, string> = {
  'left-hand-on-top': storagePrefixFixedV2 + '/caramer-mark/left-hand-on-top.png',
  'right-hand-on-top': storagePrefixFixedV2 + '/caramer-mark/right-hand-on-top.png',
  'hands-on-back': storagePrefixFixedV2 + '/caramer-mark/hands-on-back.png',
  'hands-up': storagePrefixFixedV2 + '/caramer-mark/hands-up.png',
  'left-hand-to-knee': storagePrefixFixedV2 + '/caramer-mark/left-hand-to-knee.png',
  'right-hand-to-knee': storagePrefixFixedV2 + '/caramer-mark/right-hand-to-knee.png',
  'fingertips-touch-ground': storagePrefixFixedV2 + '/caramer-mark/fingertips-touch-ground.png',
};

const CAMERA_MARK_IMAGE_WIDTH: Record<CameraMarkType, number> = {
  'left-hand-on-top': 413,
  'right-hand-on-top': 413,
  'hands-on-back': 389,
  'hands-up': 258,
  'left-hand-to-knee': 475,
  'right-hand-to-knee': 475,
  'fingertips-touch-ground': 441,
};

interface Props {
  afterMassage?: boolean;
  beforeHandData: { value: string; text?: string }[];
  afterWardsData?: { value: string; text?: string }[];
  onImgsChange: (imgs: { value: string }[]) => void;
  actionTestItems: ActionTest.Questionnaire['action_test'];
}

const MotionTestCard = ({
  afterMassage = false,
  beforeHandData,
  afterWardsData,
  actionTestItems,
  onImgsChange,
}: Props) => {
  const [uploadFileUrls, setUploadFileUrls] = useState([]);

  const imagePreviewContainer = useRef(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const { handleScan } = useCustomCamera();

  const handleDeleteImg = (index: number) => {
    const newUrls = klona(uploadFileUrls);
    newUrls[index] = { value: '' };
    setUploadFileUrls(newUrls);

    Toast.show('已删除该图片');
  };

  // 回显之前设置的疼痛记录值/初始化数据
  const backToShowData = useCallback(() => {
    const len = actionTestItems?.length;
    if (!len) return;

    try {
      // 动作复测
      if (afterMassage) {
        if (afterWardsData?.length) {
          setUploadFileUrls(afterWardsData?.map(item => (typeof item === 'string' ? JSON.parse(item) : item)));
        } else {
          setUploadFileUrls(new Array(len).fill({ value: '' }));
        }
      } else {
        // 动作测试
        if (beforeHandData?.length) {
          const actionImgs = beforeHandData.slice(0, len);
          setUploadFileUrls(actionImgs?.map(item => (typeof item === 'string' ? JSON.parse(item) : item)));
        } else {
          setUploadFileUrls(new Array(len).fill({ value: '' }));
        }
      }
    } catch {}
  }, [actionTestItems?.length, afterMassage, beforeHandData, afterWardsData]);

  useEffect(() => {
    backToShowData();
  }, [backToShowData]);

  useEffect(() => {
    onImgsChange(uploadFileUrls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileUrls]);

  return (
    <div className={cx('motion-test-card')}>
      <div className={cx('tips')}>
        <span className={cx('title')}>{afterMassage ? '动作复测' : '动作测试'}</span>
        <img src={Guide} alt="guide" />
        <span className={cx('desc')}>
          {afterMassage
            ? '请再次按照以下动作提示进行动作测试，并拍照上传，对比理疗前后的身体状态'
            : '请引导用户按照提示完成以下动作测试，并通过平板拍照上传。'}
        </span>
      </div>

      <div className={cx('motions')}>
        {actionTestItems?.map((item, index) => (
          <div className={cx('motion-item')} key={item.title}>
            <div className={cx('item')}>
              <Image src={item.sample_image} alt="motion-item" preview={{ maxScale: 5 }} />
              <div className={cx('tip', 'example-tip')}>{item.title}</div>
            </div>
            <ImagePreviewCompare
              beforeImgList={beforeHandData?.map(item => item?.value) || []}
              afterImgList={uploadFileUrls?.map(item => item?.value) || []}
              previewIndex={previewIndex}
              updatePreviewIndex={setPreviewIndex}
              isNeedImgGroup={!!afterMassage}
              totalCompareLength={actionTestItems?.length}
            >
              <>
                {afterMassage && (
                  <div className={cx('item', 'pre-upload')}>
                    {beforeHandData?.[index]?.value ? (
                      <>
                        <Image
                          onClick={() => setPreviewIndex(index)}
                          preview={{ maxScale: 5 }}
                          className={cx('upload-img')}
                          src={beforeHandData[index]?.value}
                          alt="motion-item"
                        />
                        <div className={cx('img-tip')}>服务前</div>
                      </>
                    ) : (
                      <div className={cx('skip-tip')}>服务前动作测试图片未上传</div>
                    )}
                  </div>
                )}

                <div className={cx('item', 'upload-box')} ref={imagePreviewContainer}>
                  <div className={cx('icon-wrapper')}>
                    {uploadFileUrls?.[index]?.value ? (
                      <>
                        <Image
                          src={uploadFileUrls?.[index]?.value}
                          alt="upload-img"
                          preview={{
                            maxScale: 5,
                            imageRender: (originalNode, info) => {
                              return (
                                <div className={cx('preview-image')}>
                                  <div className={cx('img-wrapper')}>{originalNode}</div>
                                  <div className={cx('tag')}>
                                    <span>服务前</span>
                                  </div>
                                </div>
                              );
                            },
                          }}
                          className={cx('upload-img')}
                          onClick={() => setPreviewIndex(index)}
                        />
                        <div className={cx('img-tip', { after: afterMassage })}>
                          {afterMassage ? '服务后' : '服务前'}
                        </div>

                        {/* 删除图标 */}
                        <img
                          src={DeleteIcon}
                          alt="delete"
                          onClick={() => {
                            handleDeleteImg(index);
                          }}
                          className={cx('delete')}
                        ></img>
                      </>
                    ) : (
                      <div
                        className={cx('add-wrapper')}
                        onClick={async () => {
                          const { url } = await handleScan({
                            width: CAMERA_MARK_IMAGE_WIDTH?.[item?.type],
                            url: CAMERA_MARK_URL?.[item?.type],
                          });

                          const newUrls = klona(uploadFileUrls);
                          newUrls[index].value = url;
                          setUploadFileUrls(newUrls);
                        }}
                      >
                        <Icon name="camera_2" size={32} />
                        <div>拍照上传</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            </ImagePreviewCompare>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MotionTestCard;
